/** @format */

// src/components/products/ProductList.jsx
import { faBoxOpen } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Switch,
  Tooltip,
} from '@mui/material';
import { DataGrid, frFR } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { api } from '../../../../api.ts';
import NewTooltip from '../../../misc/NewTooltip';
import { getPrefixedInternalNameLabel } from './productName';

function KitLinesTooltip({ productId }) {
  const [open, setOpen] = useState(false);
  const [kitLines, setKitLines] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    if (!loading) {
      setLoading(true);
      api
        .get(`/products/v2/admin-products/${productId}/`)
        .then((res) => {
          setKitLines(res.data.kit_lines || []);
        })
        .finally(() => setLoading(false));
    }
  };

  const handleClose = () => setOpen(false);

  let tooltipContent;
  if (loading) {
    tooltipContent = <CircularProgress size={18} />;
  } else if (kitLines && kitLines.length > 0) {
    tooltipContent = (
      <div>
        {kitLines.map((line) => (
          <div key={line.id}>
            {(line.product?.internal_name || line.product) +
              ' × ' +
              line.quantity}
          </div>
        ))}
      </div>
    );
  } else {
    tooltipContent = 'Aucune ligne de kit';
  }

  return (
    <NewTooltip
      content={tooltipContent}
      placement="right"
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      arrow
    >
      <IconButton size="small">
        <FontAwesomeIcon icon={faBoxOpen} />
      </IconButton>
    </NewTooltip>
  );
}

function ProductList({
  products,
  isLoading,
  onEdit,
  onDelete,
  paginationModel,
  setPaginationModel,
  rowCount,
  onProcessRowUpdate,
  sortModel,
  onSortModelChange,
}) {
  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'is_kit',
      headerName: '',
      width: 50,
      sortable: false,
      filterable: false,
      align: 'center',
      renderCell: (params) =>
        params.row.is_kit ? (
          <KitLinesTooltip productId={params.row.id} />
        ) : null,
    },
    {
      field: 'public_name',
      headerName: 'Nom public',
      flex: 1.5,
      minWidth: 200,
      valueGetter: ({ row }) =>
        row.is_validated
          ? row.public_name || row.custom_public_name || 'Non renseigné'
          : row.custom_public_name || row.public_name || 'Non renseigné',
    },
    {
      field: 'prefixed_internal_name',
      headerName: 'Nom interne',
      flex: 1,
      minWidth: 150,
      valueGetter: ({ row }) => getPrefixedInternalNameLabel(row),
    },
    {
      field: 'brand',
      headerName: 'Marque',
      flex: 0.5,
      minWidth: 100,
      valueGetter: ({ row }) => row?.brand?.name || '',
    },
    {
      field: 'sub_categories',
      headerName: 'Sous-catégories',
      flex: 0.7,
      minWidth: 120,
      valueGetter: ({ row }) =>
        row?.sub_categories.map((sc) => sc?.name).join(', ') || 'N/A',
    },
    {
      field: 'typology',
      headerName: 'Typologie',
      flex: 0.7,
      minWidth: 120,
      valueGetter: ({ row }) => row?.typology?.name || 'N/A',
    },
    {
      field: 'is_active',
      headerName: 'Actif',
      width: 110,
      type: 'boolean',
      editable: true,
      renderCell: (params) =>
        params.value ? (
          <Chip
            label="Actif"
            color="primary"
            variant="filled"
            size="small"
            sx={{ pt: 0.5 }}
          />
        ) : (
          <Chip
            label="Inactif"
            color="default"
            variant="outlined"
            size="small"
            sx={{ pt: 0.5 }}
          />
        ),
      renderEditCell: (params) => (
        <Switch
          checked={!!params.value}
          onChange={(event) => {
            params.api.setEditCellValue(
              {
                id: params.id,
                field: params.field,
                value: event.target.checked,
              },
              event,
            );
          }}
          autoFocus
        />
      ),
    },
    {
      field: 'is_validated',
      headerName: 'Validé',
      width: 110,
      type: 'boolean',
      renderCell: (params) =>
        params.value ? (
          <Chip
            label="Validé"
            color="primary"
            variant="filled"
            size="small"
            sx={{ pt: 0.5 }}
          />
        ) : (
          <Chip
            label="Non validé"
            color="default"
            variant="outlined"
            size="small"
            sx={{ pt: 0.5 }}
          />
        ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="100%"
          height="100%"
        >
          <Tooltip title="Modifier le produit">
            <IconButton onClick={() => onEdit(params.row)} color="primary">
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      headerAlign: 'center',
    },
  ];

  return (
    <Box sx={{ height: '70vh', width: '100%' }}>
      <DataGrid
        rows={products}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        paginationMode="server"
        rowCount={rowCount}
        disableRowSelectionOnClick
        processRowUpdate={onProcessRowUpdate}
        experimentalFeatures={{ newEditingApi: true }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
        sx={{
          '& .MuiDataGrid-footerContainer': {
            paddingRight: '60px',
          },
        }}
      />
    </Box>
  );
}

ProductList.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      public_name: PropTypes.string.isRequired,
      internal_name: PropTypes.string.isRequired,
      brand: PropTypes.shape({
        name: PropTypes.string,
      }),
      sub_categories: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
        }),
      ),
      typology: PropTypes.shape({
        name: PropTypes.string,
      }),
      is_active: PropTypes.bool,
      is_draft: PropTypes.bool,
    }),
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  paginationModel: PropTypes.shape({
    page: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
  }).isRequired,
  setPaginationModel: PropTypes.func.isRequired,
  rowCount: PropTypes.number.isRequired,
  onProcessRowUpdate: PropTypes.func,
  sortModel: PropTypes.array.isRequired,
  onSortModelChange: PropTypes.func.isRequired,
};

ProductList.defaultProps = {
  onProcessRowUpdate: undefined,
};

export default ProductList;
